/**
 * Rhythmz — Scene: Particle Nebula
 * ──────────────────────────────────
 * 5000 glowing particles forming a spiraling nebula. Bursts on beat,
 * contracts/expands with bass. Color cycles hot pink/cyan/gold.
 *
 * Audio: Bass → attract/repel. Beat → radial burst. Mid → rotation speed.
 */

import * as THREE from 'three/webgpu';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface NebulaUniforms { uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N; uCol0: N; uCol1: N; uCol2: N; }

const PARTICLE_COUNT = 8000;

export function createParticleNebula(u: NebulaUniforms): SceneHandle {
  const group = new THREE.Group();

  // ── Particle positions + base positions (spring-back target) ──
  const positions = new Float32Array(PARTICLE_COUNT * 3);
  const basePositions = new Float32Array(PARTICLE_COUNT * 3);
  const colors = new Float32Array(PARTICLE_COUNT * 3);
  const sizes = new Float32Array(PARTICLE_COUNT);
  const velocities = new Float32Array(PARTICLE_COUNT);
  // Per-particle burst offset (decays to 0 over time)
  const burstOffsets = new Float32Array(PARTICLE_COUNT * 3);

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const r = 1.0 + Math.random() * 2.5; // larger base shell
    const px = r * Math.sin(phi) * Math.cos(theta);
    const py = r * Math.cos(phi) * 0.5;
    const pz = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3] = px;
    positions[i * 3 + 1] = py;
    positions[i * 3 + 2] = pz;
    basePositions[i * 3] = px;
    basePositions[i * 3 + 1] = py;
    basePositions[i * 3 + 2] = pz;
    const brightness = 0.5 + Math.random() * 0.5;
    colors[i * 3] = brightness;
    colors[i * 3 + 1] = brightness * 0.3;
    colors[i * 3 + 2] = brightness * 0.7;
    sizes[i] = 0.02 + Math.random() * 0.04;
    velocities[i] = 0.5 + Math.random() * 1.5;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const mat = new THREE.PointsMaterial({
    size: 0.08,
    vertexColors: true,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  const points = new THREE.Points(geo, mat);
  group.add(points);

  let t = 0;

  const update = (dt: number): void => {
    t += dt;
    const bass = u.uBass.value ?? 0;
    const mid = u.uMid.value ?? 0;
    const beat = u.uBeat.value ?? 0;

    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const col = geo.getAttribute('color') as THREE.BufferAttribute;

    const speed = 0.5 + mid * 1.5;
    const burst = beat * 0.5;

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      let x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const bx = basePositions[i * 3], by = basePositions[i * 3 + 1], bz = basePositions[i * 3 + 2];

      // Spiral rotation of base position
      const angle = dt * speed * (0.3 + velocities[i] * 0.1);
      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      const nbx = bx * cosA - bz * sinA;
      const nbz = bx * sinA + bz * cosA;
      basePositions[i * 3] = nbx;
      basePositions[i * 3 + 1] = by;
      basePositions[i * 3 + 2] = nbz;

      // Bass push: temporarily expand radius outward
      const bassExpand = 1 + bass * 0.4;
      const targetX = nbx * bassExpand;
      const targetY = by * bassExpand;
      const targetZ = nbz * bassExpand;

      // Spring toward target (lerp)
      const spring = 0.92; // how fast particles return, 1.0 = instant
      x += (targetX - x) * (1 - spring);
      y += (targetY - y) * (1 - spring);
      z += (targetZ - z) * (1 - spring);

      // Beat burst: add to burst offset
      if (beat > 0.1) {
        const r = Math.sqrt(x * x + y * y + z * z) + 0.001;
        burstOffsets[i * 3] += (x / r) * burst * 0.05;
        burstOffsets[i * 3 + 1] += (y / r) * burst * 0.05;
        burstOffsets[i * 3 + 2] += (z / r) * burst * 0.05;
      }
      // Decay burst offset
      burstOffsets[i * 3] *= 0.95;
      burstOffsets[i * 3 + 1] *= 0.95;
      burstOffsets[i * 3 + 2] *= 0.95;

      // Apply burst offset
      x += burstOffsets[i * 3];
      y += burstOffsets[i * 3 + 1];
      z += burstOffsets[i * 3 + 2];

      pos.setXYZ(i, x, y, z);

      // Color cycling — use current radius for brightness
      const r = Math.sqrt(x * x + y * y + z * z);
      const colorT = (t * 0.05 + i * 0.001) % 1;
      const cr = 0.5 + Math.sin(colorT * 6.28) * 0.5;
      const cg = 0.5 + Math.sin(colorT * 6.28 + 2.094) * 0.5;
      const cb = 0.5 + Math.sin(colorT * 6.28 + 4.188) * 0.5;
      const bright = 0.5 + r / 6 + beat * 0.5;
      col.setXYZ(i, cr * bright, cg * bright * 0.5, cb * bright);
    }
    pos.needsUpdate = true;
    col.needsUpdate = true;

    group.rotation.y = t * 0.02;
  };

  const dispose = (): void => {
    geo.dispose(); mat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Particle Nebula' };
}